/**
 * YouDoScreen - K Comparison You Do Practice
 *
 * Independent practice where user selects the greater number.
 * Plays TTS instruction, tracks scores, retries failed facts.
 * Updates backend status to accuracyPractice when fact is mastered.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { NUMERAL_FONT_STYLE } from '../../../styles/design-system/typography';
import { KPracticeSettings } from '../types';
import { ComparisonFact } from './types';
import { useKTTS, K_QUESTION_SCREEN_AUDIO } from '../../../hooks/useKAudio';
import { useAuth } from '../../../context/AuthContext';
import { setUserProgress } from '../../../config/api';
import { SetUserProgressRequest } from '../../../types/progress';

// Use PreK shared components
import {
  PracticeHeader,
  PRACTICE_PHASES,
} from '../../PreKPractice/shared';

interface YouDoScreenProps {
  settings: KPracticeSettings;
  factsToPractice: ComparisonFact[];
  onComplete: () => void;
  onBack: () => void;
}

const YouDoScreen: React.FC<YouDoScreenProps> = ({
  settings,
  factsToPractice,
  onComplete,
  onBack,
}) => {
  const { user } = useAuth();
  const [currentFactIndex, setCurrentFactIndex] = useState(0);
  const [completedCount, setCompletedCount] = useState(0);
  const [failedQueue, setFailedQueue] = useState<ComparisonFact[]>([]);
  const [isRetryPhase, setIsRetryPhase] = useState(false);
  const [currentRetryIndex, setCurrentRetryIndex] = useState(0);
  const [factScores, setFactScores] = useState<Map<string, number>>(new Map());

  const [hasAnswered, setHasAnswered] = useState(false);
  const [wrongAnswer, setWrongAnswer] = useState<number | null>(null);
  const [ttsHasPlayed, setTtsHasPlayed] = useState(false);
  const [correctAnswerGiven, setCorrectAnswerGiven] = useState(false);

  const timeoutsRef = useRef<NodeJS.Timeout[]>([]);

  const { playChime, playTTSWithCallback, isTTSPlaying } = useKTTS();

  // Get current fact
  const getCurrentFact = useCallback((): ComparisonFact | undefined => {
    if (isRetryPhase) {
      return failedQueue[currentRetryIndex];
    }
    return factsToPractice[currentFactIndex];
  }, [isRetryPhase, failedQueue, currentRetryIndex, factsToPractice, currentFactIndex]);

  const currentFact = getCurrentFact();

  // Reset state when fact changes
  useEffect(() => {
    timeoutsRef.current.forEach(clearTimeout);
    timeoutsRef.current = [];
    setHasAnswered(false);
    setWrongAnswer(null);
    setTtsHasPlayed(false);
    setCorrectAnswerGiven(false);
  }, [currentFactIndex, isRetryPhase, currentRetryIndex]);

  // Play TTS instruction when question loads
  useEffect(() => {
    if (!currentFact || ttsHasPlayed) return;

    if (settings.speechEnabled) {
      // Play "Which is greater? Tap the bigger number."
      playTTSWithCallback(K_QUESTION_SCREEN_AUDIO.comparing.weDoIntro, () => {
        setTtsHasPlayed(true);
      }).catch(() => {
        setTtsHasPlayed(true);
      });
    } else {
      setTtsHasPlayed(true);
    }
  }, [currentFact, settings.speechEnabled, playTTSWithCallback, ttsHasPlayed]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      timeoutsRef.current.forEach(clearTimeout);
    };
  }, []);

  const advanceToNext = useCallback(() => {
    if (isRetryPhase) {
      // In retry phase - after removing an item, check if queue is empty
      if (failedQueue.length === 0) {
        // Queue is empty, we're done!
        onComplete();
      } else if (currentRetryIndex >= failedQueue.length) {
        // Current index is beyond queue length, reset to 0
        setCurrentRetryIndex(0);
      } else if (currentRetryIndex < failedQueue.length - 1) {
        // Move to next item in retry queue
        setCurrentRetryIndex(currentRetryIndex + 1);
      } else {
        // At end of retry queue, restart from beginning
        setCurrentRetryIndex(0);
      }
    } else {
      // In original phase
      if (currentFactIndex < factsToPractice.length - 1) {
        setCurrentFactIndex(currentFactIndex + 1);
      } else {
        // Finished original sequence
        if (failedQueue.length > 0) {
          // Start retry phase
          setIsRetryPhase(true);
          setCurrentRetryIndex(0);
        } else {
          // All done!
          onComplete();
        }
      }
    }

    // Reset answer state for new question
    setHasAnswered(false);
    setWrongAnswer(null);
    setCorrectAnswerGiven(false);
    setTtsHasPlayed(false);
  }, [isRetryPhase, currentRetryIndex, failedQueue.length, currentFactIndex, factsToPractice.length, onComplete]);

  const handleAnswerClick = useCallback(
    (selectedNumber: number) => {
      if (!currentFact) return;

      // Don't allow clicks while TTS is playing or already answered
      if (isTTSPlaying || hasAnswered) return;

      setHasAnswered(true);
      const isCorrect = selectedNumber === currentFact.result;

      if (isCorrect) {
        setCorrectAnswerGiven(true);

        // Update fact score: +1 for correct
        setFactScores(prev => {
          const newScores = new Map(prev);
          const currentScore = newScores.get(currentFact.factId) || 0;
          const newScore = currentScore + 1;
          newScores.set(currentFact.factId, newScore);

          // Only increment counter if going from 0 to 1 (first genuine correct)
          if (currentScore === 0 && newScore === 1) {
            setCompletedCount(prevCount => prevCount + 1);

            // Update backend fact status to accuracyPractice when score reaches 1
            if (user?.userId) {
              setTimeout(async () => {
                try {
                  const progressUpdate: SetUserProgressRequest = {
                    facts: {
                      [currentFact.factId]: {
                        status: 'accuracyPractice'
                      }
                    }
                  };

                  await setUserProgress(user.userId, progressUpdate);
                } catch (error) {
                  console.error('Failed to update fact status to accuracyPractice:', error);
                }
              }, 0);
            }
          }

          return newScores;
        });

        if (isRetryPhase) {
          // In retry phase - remove from failed queue
          setFailedQueue(prev => {
            const newQueue = prev.filter((_, index) => index !== currentRetryIndex);
            // Check if we need to complete after this removal
            if (newQueue.length === 0) {
              setTimeout(() => onComplete(), 600);
            } else {
              // Adjust currentRetryIndex after removal
              setTimeout(() => {
                if (currentRetryIndex >= newQueue.length) {
                  setCurrentRetryIndex(0);
                }
              }, 600);
            }
            return newQueue;
          });
        }

        // Play chime and advance after a delay (but not in retry phase where we handle advancement in setFailedQueue)
        playChime().then(() => {
          if (!isRetryPhase) {
            setTimeout(() => {
              advanceToNext();
            }, 500);
          }
        });
      } else {
        // Incorrect answer
        setWrongAnswer(selectedNumber);

        // Update fact score: -1 for incorrect
        setFactScores(prev => {
          const newScores = new Map(prev);
          const currentScore = newScores.get(currentFact.factId) || 0;
          const newScore = currentScore - 1;
          newScores.set(currentFact.factId, newScore);

          // If score goes below 1, decrement counter (was previously counted but now incorrect)
          if (currentScore >= 1 && newScore < 1) {
            setCompletedCount(prevCount => prevCount - 1);
          }

          return newScores;
        });

        if (!correctAnswerGiven) {
          // First attempt was wrong - add to failed queue (only if not already there)
          if (!isRetryPhase) {
            setFailedQueue(prev => {
              const alreadyExists = prev.some(f => f.factId === currentFact.factId);
              if (!alreadyExists) {
                return [...prev, currentFact];
              }
              return prev;
            });
          } else {
            // In retry phase and still wrong - add back to end of failed queue
            const failedFact = failedQueue[currentRetryIndex];
            setFailedQueue(prev => [...prev.filter((_, index) => index !== currentRetryIndex), failedFact]);
          }
        }

        // Reset to allow another attempt after feedback
        const timeout = setTimeout(() => {
          setHasAnswered(false);
          setWrongAnswer(null);
        }, 1500);
        timeoutsRef.current.push(timeout);
      }
    },
    [
      currentFact,
      hasAnswered,
      isTTSPlaying,
      playChime,
      correctAnswerGiven,
      isRetryPhase,
      failedQueue,
      currentRetryIndex,
      advanceToNext,
      onComplete,
      user
    ]
  );

  if (!currentFact) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100 flex items-center justify-center">
        <div className="text-2xl text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100 flex flex-col">
      <PracticeHeader
        phase={PRACTICE_PHASES.YOU_DO}
        onBack={onBack}
        progress={
          factsToPractice.length > 0
            ? Math.min((completedCount) / factsToPractice.length, 1)
            : 0
        }
      />

      {/* Main Content Area */}
      <div className="flex-1 flex items-center justify-center px-6 py-6">
        <div className="w-full max-w-md lg:max-w-lg">
          {/* Question prompt */}
          <div className="text-center mb-8">
            <div className="text-2xl lg:text-3xl text-gray-700 font-semibold">
              Which is greater?
            </div>
            {isRetryPhase && (
              <div className="text-sm text-orange-500 mt-2">Let's try these again!</div>
            )}
          </div>

          {/* Two number choices stacked vertically */}
          <div className="flex flex-col gap-6">
            {[currentFact.operand1, currentFact.operand2].map((num, index) => {
              const isCorrect = num === currentFact.result;
              const isWrong = wrongAnswer === num;
              const showAsCorrect = hasAnswered && isCorrect;

              return (
                <motion.button
                  key={`${currentFact.factId}-${index}`}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  whileHover={!hasAnswered && !isTTSPlaying ? { scale: 1.05 } : {}}
                  whileTap={!hasAnswered && !isTTSPlaying ? { scale: 0.95 } : {}}
                  onClick={() => handleAnswerClick(num)}
                  disabled={hasAnswered || isTTSPlaying}
                  className={`
                    h-32 lg:h-40 rounded-3xl font-bold text-5xl lg:text-6xl shadow-xl
                    transition-all duration-300
                    ${
                      isWrong
                        ? 'bg-red-500 text-white border-4 border-red-700 animate-pulse'
                        : showAsCorrect
                        ? 'bg-green-500 text-white border-4 border-green-700'
                        : hasAnswered && !isCorrect
                        ? 'bg-gray-300 text-gray-500 border-4 border-gray-400'
                        : isTTSPlaying
                        ? 'bg-gray-300 text-gray-500 border-4 border-gray-400 cursor-not-allowed'
                        : 'bg-gradient-to-br from-blue-400 to-blue-600 hover:from-blue-500 hover:to-blue-700 text-white border-4 border-blue-700 cursor-pointer'
                    }
                    ${hasAnswered || isTTSPlaying ? 'cursor-not-allowed' : 'hover:shadow-2xl'}
                  `}
                  style={NUMERAL_FONT_STYLE}
                >
                  {num}
                </motion.button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Audio Playing Indicator */}
      {isTTSPlaying && (
        <div className="flex justify-center pb-8">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex items-center text-gray-600"
          >
            <div className="animate-pulse mr-2">🔊</div>
            <span>Listen carefully...</span>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default YouDoScreen;
